// src/pages/WatchlistDetailPage.jsx
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Download,
  Send,
  Trash2,
  Search,
  CheckCircle,
  X,
  PiggyBank,
  PlusCircle,
  HandCoins,
} from "lucide-react";

export default function WatchlistDetailPage() {
  const { id, firma: urlFirma, pharmacyCode: urlPC } = useParams();
  const { user } = useAuth();

  const firma = urlFirma || user?.firma || "LEMON";
  const pharmacyCode = urlPC || user?.pharmacyCode || null;

  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const resultsRef = useRef();

  const load = async () => {
    if (!pharmacyCode) return;

    setLoading(true);
    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/resolve`,
        { credentials: "include" }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Chyba načítání");

      json.items = [...json.items].sort((a, b) =>
        (a.nazev || "").localeCompare(b.nazev || "", "cs")
      );
      setData(json);
    } catch (e) {
      console.error("Chyba:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [id, user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchItems = async () => {
    if (search.length < 2) return setResults([]);
    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/search?q=${encodeURIComponent(
          search
        )}`,
        { credentials: "include" }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setResults(json);
    } catch (e) {
      console.error(e);
    }
  };

  const addItem = async (sukl) => {
    try {
      await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/items`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sukl }),
        }
      );
      load();
    } catch {
      alert("Chyba při přidávání.");
    }
  };

  const removeItem = async (sukl) => {
    if (!window.confirm("Odebrat přípravek?")) return;
    try {
      await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/items/${sukl}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      load();
    } catch {
      alert("Chyba při mazání.");
    }
  };

  const toggleBezDoplatku = async (sukl, current) => {
    try {
      await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/items/${sukl}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bezDoplatku: !current }),
        }
      );
      load();
    } catch {
      alert("Chyba při změně příznaku.");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/export`,
        { credentials: "include" }
      );

      if (!res.ok) throw new Error("Chyba exportu");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      console.error(e);
      alert("Chyba při stahování PDF.");
    }
  };

  const sendEmail = async () => {
    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}/export`,
        { credentials: "include" }
      );

      if (!res.ok) throw new Error("Chyba exportu");
      const blob = await res.blob();

      const filename = `${data.name.replace(/\s+/g, "_")}.pdf`;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      const to = encodeURIComponent(data.email || "");
      const subject = encodeURIComponent("Vybrané doplatky – LEMON");
      const body = encodeURIComponent(
        "Dobrý den,\n\nv příloze zasílám přehled doplatků.\n\nS pozdravem,\nLEMON"
      );

      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    } catch (e) {
      console.error(e);
      alert("Nelze připravit e-mail.");
    }
  };

  const formatNum = (v) =>
    v == null || isNaN(v) ? "—" : Number(v).toFixed(2).replace(".", ",");

  if (loading) return <div className="p-4">Načítám…</div>;
  if (!data) return <div className="p-4">Watchlist nenalezen.</div>;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">{data.name}</h1>
        <div className="flex gap-2">
          <button onClick={downloadPdf} className="bg-gray-200 px-3 py-2 rounded flex items-center gap-1">
            <Download size={18} /> PDF
          </button>
          <button onClick={sendEmail} className="bg-gray-200 px-3 py-2 rounded flex items-center gap-1">
            <Send size={18} /> E-mail
          </button>
        </div>
      </div>

      {/* SEARCH */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          searchItems();
        }}
        className="mb-4 flex gap-2"
      >
        <input
          type="text"
          placeholder="Vyhledat podle názvu nebo kódu"
          className="border p-2 flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="bg-blue-600 text-white px-3 py-2 rounded flex items-center gap-1">
          <Search size={18} /> Hledat
        </button>
      </form>

      {/* SEARCH RESULTS */}
      {results.length > 0 && (
        <div ref={resultsRef} className="mb-4 border p-2 bg-white shadow">
          <div className="flex justify-between items-center mb-2">
            <h2 className="font-semibold">Výsledky hledání</h2>
            <button
              onClick={() => setResults([])}
              className="text-gray-500 flex items-center gap-1 text-sm"
            >
              <X size={14} /> Zavřít
            </button>
          </div>

          <ul>
            {results.map((r) => {
              const exists = data.items.some((it) => it.sukl === r.sukl);
              return (
                <li
                  key={r.sukl}
                  className="flex justify-between items-center border-t py-1"
                >
                  <span>
                    {String(r.sukl).replace(/^0+/, "")} — {r.nazev}
                  </span>

                  {exists ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle size={16} /> Přidáno
                    </span>
                  ) : (
                    <button
                      className="text-blue-600 flex items-center gap-1"
                      onClick={() => addItem(r.sukl)}
                    >
                      <PlusCircle size={16} /> Přidat
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* TABLE */}
      <table className="w-full border text-sm">
        <thead>
          <tr className="bg-gray-100 text-center">
            <th className="p-2 w-24">KÓD VZP</th>
            <th className="p-2">NÁZEV</th>
            <th className="p-2 w-32">DOPLATEK<br/><span className="text-xs text-gray-500">(bez limitu)</span></th>
            <th className="p-2 w-32">DOPLATEK<br/><span className="text-xs text-gray-500">(limit dosažen)</span></th>
            <th className="p-2 w-32">ZVÝŠ. ÚHRADA<br/><span className="text-xs text-gray-500">(bez limitu)</span></th>
            <th className="p-2 w-32">ZVÝŠ. ÚHRADA<br/><span className="text-xs text-gray-500">(limit dosažen)</span></th>
            <th className="p-2 w-16">Akce</th>
          </tr>
        </thead>

        <tbody>
          {data.items.map((it) => (
            <tr key={it.sukl} className="border-t">
              <td className="p-2">{String(it.sukl).replace(/^0+/, "")}</td>
              <td className="p-2">{it.nazev || "—"}</td>

              <td className="p-2 text-right pr-4">{formatNum(it.doplatekPacientaZK)} Kč</td>
              <td className="p-2 text-right pr-4">{formatNum(it.doplatekPacientaLimitZK)} Kč</td>
              <td className="p-2 text-right pr-4">{formatNum(it.doplatekZvysenaZK)} Kč</td>
              <td className="p-2 text-right pr-4">{formatNum(it.doplatekZvysenaLimitZK)} Kč</td>

              <td className="p-2 text-center">
                <div className="inline-flex items-center justify-center gap-2">

                  {/* Bez doplatku toggle */}
                  <button
                    title={it.bezDoplatku ? "Zrušit Bez doplatku" : "Nastavit Bez doplatku"}
                    onClick={() => toggleBezDoplatku(it.sukl, it.bezDoplatku)}
                    className="text-blue-600 hover:text-green-700"
                  >
                    {it.bezDoplatku ? <HandCoins size={18} /> : <PiggyBank size={18} />}
                  </button>

                  {/* Mazání */}
                  <button
                    className="text-red-600"
                    title="Odebrat"
                    onClick={() => removeItem(it.sukl)}
                  >
                    <Trash2 size={18} />
                  </button>

                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
